import React, { Fragment, useEffect, useState } from 'react';
import GridCellWrapper from '../../../components/grid-cell-wrapper/GridCellWrapper';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import Button from '../../../components/button/Button';
import Toolbar from '../../../components/toolbar/Toolbar';
import Modal from '../../../components/modal/Modal';
import Benutzererfassung from '../benutzererfassung/Benutzererfassung';
import Table from '../../../components/table/Table';
import { initUsermanagementTableConfig } from './UsermanagementTableConfig';

import { Benutzer } from '../../../api';
import { connect } from 'react-redux';
import {
  getCurrentBenutzer,
  searchBenutzer,
} from '../../../redux/actions/benutzer';
import { utcIsoDateToLocaleDate } from '../../../utils/date';
import {
  resetBenutzererfassung,
  setBenutzerValue,
} from '../../../redux/actions/benutzererfassung';
import { UserSessionModel } from '../../../models/UserSessionModel';
import { setNotification } from '../../../utils/notificationUtils';
import { INFO } from '../../notification/Notification';
import { StateModel, StateModel_Benutzererfassung } from '../../../redux/store';
import InputFieldText from '../../../components/input/floating-label/input-field/InputFieldText';
import { changeLogin } from '../../../redux/actions/login';
import { nameof } from '../../../utils/objectUtils';
import Pagination from '../../../components/pagination/Pagination';

interface UsermanagementProps {
  getCurrentBenutzer: () => void;
  searchBenutzer: (
    searchParam: string,
    page: number,
    searchLimit: number
  ) => void;
  benutzerliste: Benutzer[];
  setBenutzerValue: (
    value: string | number | undefined,
    fieldName: string
  ) => void;
  resetBenutzererfassung: () => void;
  benutzererfassung: StateModel_Benutzererfassung;
  reloadToggle: boolean;
  userSession: UserSessionModel;
  changeLogin: (name: string) => void;
  totalSearchResults: any;
}

/**
 * Usermanagement-Component
 *
 * @returns {JSX.Element} Usermanagement-Component
 * @constructor
 */
const Usermanagement = (props: UsermanagementProps & RouteComponentProps) => {
  const [query, setQuery] = useState<string>('');
  const [showModalEdit, setShowModalEdit] = useState(false);
  const [data, setData] = useState<Array<any>>([{}]);
  const [selectedRow, setSelectedRow] = useState<any>(undefined);
  const [tableConfig, setTableConfig] = useState(
    initUsermanagementTableConfig()
  );
  const [page, setPage] = useState(1);

  const search = (page: number) => {
    setPage(page);

    props.searchBenutzer(query, page, +props.userSession.rowsInGrid);
  };

  /**
   * Steuert die Schließung vom Modalen Fenster und leert den State der Felder.
   */
  const handleClose = () => {
    props.resetBenutzererfassung();
    setShowModalEdit(false);
  };

  const openBenutzererfassung = () => {
    setShowModalEdit(true);
  };

  useEffect(() => {
    search(page);
  }, [props.reloadToggle]);

  useEffect(() => {
    search(1);
  }, [query]);

  useEffect(() => {
    setData(props.benutzerliste ? convertBenutzerIntoResearchList() : [{}]);
  }, [props.benutzerliste]);

  const onEditBenutzer = () => {
    if (selectedRow || selectedRow == 0) {
      const benutzerId: number = data[selectedRow].id;
      if (benutzerId) {
        props.setBenutzerValue(
          benutzerId,
          nameof<StateModel_Benutzererfassung>('id')
        );
        openBenutzererfassung();
      }
    } else {
      setNotification('Bitte markieren Sie eine Zeile.', [], INFO, 5000);
    }
  };

  const onChangeBenutzer = () => {
    if (selectedRow || selectedRow == 0) {
      const benutzerId: number = data[selectedRow].id;
      if (benutzerId) {
        props.setBenutzerValue(benutzerId, 'id');
        const benutzerName: string = data[selectedRow].name;
        props.changeLogin(benutzerName);
      }
    } else {
      setNotification('Bitte markieren Sie eine Zeile.', [], INFO, 5000);
    }
  };

  /**
   * Konvertiert Benutzer in das UsermanagementTableModel zur visualisierung in der Tabelle
   */
  const convertBenutzerIntoResearchList = (): Array<any> => {
    let tmpList = [...props.benutzerliste];
    tmpList = tmpList.sort((a, b) => {
      if (a?.name && b?.name) {
        return a.name
          .toLowerCase()
          ?.localeCompare(b.name.toLowerCase(), 'de-DE');
      }
      return a.name ? -1 : 1;
    });
    return tmpList.map((benutzer, index: number) => {
      index++;
      return {
        id: benutzer.id,
        name: benutzer.name,
        email: benutzer.email,
        matchcode: benutzer.owningMandant?.adresse?.matchcode,
        adressenname: benutzer.owningMandant?.adresse?.name,
        strasse: benutzer.owningMandant?.adresse?.strasse,
        plz: benutzer.owningMandant?.adresse?.plz,
        ort: benutzer.owningMandant?.adresse?.ort,
        erstelltAm: utcIsoDateToLocaleDate(benutzer.created ?? ''),
        aktualisiertAm: utcIsoDateToLocaleDate(benutzer.changed ?? ''),
      };
    });
  };

  return (
    <Fragment>
      <div className='usermanagement-wrapper'>
        <Toolbar>
          <Button
            tooltip='Benutzer hinzufügen'
            classes={'iconButton'}
            key={0}
            onClickHandler={() => openBenutzererfassung()}
          >
            <i className='fas fa-user-plus fa-lg' />
          </Button>
          <Button
            tooltip='Benutzer bearbeiten'
            classes={'iconButton'}
            key={1}
            onClickHandler={() => onEditBenutzer()}
          >
            <i className='fas fa-user-edit fa-lg' />
          </Button>
          {props.userSession.isAdmin && (
            <Button
              tooltip='zum Benutzer wechseln'
              classes={'iconButton'}
              key={2}
              onClickHandler={() => onChangeBenutzer()}
            >
              <i className='fas fa-people-arrows fa-2x'></i>
            </Button>
          )}
          <div className={'search-field-wrapper'}>
            <i className='fas fa-search fa-lg' />
            <InputFieldText
              label='Name, E-Mail, PLZ, Ort, etc.'
              name='search'
              classes='search-field'
              onChangeHandler={setQuery}
              maxlength={200}
              value={query}
            />
          </div>
        </Toolbar>
        <GridCellWrapper classes='usermanagement' label='Benutzermanagement'>
          <Table
            tableConfig={tableConfig}
            setTableConfig={setTableConfig}
            data={data}
            tabSkipIndices={[0]}
            edit={false}
            selectedRow={selectedRow}
            setSelectedRow={setSelectedRow}
            doubleClickRowHandler={onEditBenutzer}
            searchQueryString={query}
          />
          <Pagination
            onClickHandler={search}
            totalCount={+props.totalSearchResults}
            countPerPage={props.userSession.rowsInGrid}
            currentPage={page}
          />
          <Modal
            show={showModalEdit}
            closeHandler={handleClose}
            closeByOutsideClick={false}
            titel={
              props.benutzererfassung.id
                ? 'Benutzer bearbeiten'
                : 'Benutzer hinzufügen'
            }
            height={props.benutzererfassung.id ? '385px' : '350px'}
            width={'500px'}
          >
            <Benutzererfassung close={handleClose} />
          </Modal>
        </GridCellWrapper>
      </div>
    </Fragment>
  );
};

const mapStateToProps = (state: StateModel) => ({
  benutzerliste: state.Benutzer.benutzerliste,
  benutzererfassung: state.Benutzererfassung,
  reloadToggle: state.Benutzer.reloadToggle,
  userSession: state.UserSession,
  totalSearchResults: state.Adressen.totalSearchResults,
});

export default connect(mapStateToProps, {
  getCurrentBenutzer,
  searchBenutzer,
  setBenutzerValue,
  resetBenutzererfassung,
  changeLogin,
})(withRouter(Usermanagement));
